import type { FavoriteTarget, FavoriteTargetType } from "@compass/shared/schema/favorite";
import { FavoriteButton } from "./FavoriteButton";

// --- Helpers ---

export function buildFavoriteTarget(type: FavoriteTargetType, id: number | string): FavoriteTarget {
    return { type, id: Number(id) } as FavoriteTarget;
}

export function getTargetLabel(type: FavoriteTargetType): string {
    const labels: Record<FavoriteTargetType, string> = {
        post: "Post",
        feedArticle: "Article",
        blogPost: "Blog Post",
        discussion: "Discussion",
        chart: "Chart",
        event: "Event",
        compassEvent: "Private Event",
        vendor: "Vendor",
        sitewideSummary: "AI Digest",
    };
    return labels[type];
}

export function getBadgeColor(type: FavoriteTargetType): string {
    const colors: Record<FavoriteTargetType, string> = {
        post: "bg-blue-100 text-blue-700",
        feedArticle: "bg-orange-100 text-orange-700",
        blogPost: "bg-purple-100 text-purple-700",
        discussion: "bg-gray-100 text-gray-700",
        chart: "bg-green-100 text-green-700",
        event: "bg-red-100 text-red-700",
        compassEvent: "bg-teal-100 text-teal-700",
        vendor: "bg-yellow-100 text-yellow-700",
        sitewideSummary: "bg-indigo-100 text-indigo-700",
    };
    return colors[type];
}

export function getLinkToByTypeId(type: FavoriteTargetType, id: number | string): string | null {
    switch (type) {
        case "post":
            return `/social/post/${id}`;
        case "feedArticle":
            return `/rss-feeds`;
        case "blogPost":
            // Trending uses numeric ID; favorites uses getLinkTo with slug access
            return `/c/blog`;
        case "chart":
            return `/visualizations/${id}`;
        case "event":
            return `/events/${id}`;
        case "compassEvent":
            return `/private-events`;
        case "vendor":
            return `/vendors/${id}`;
        case "sitewideSummary":
            return `/digest/${id}`;
        default:
            return null;
    }
}

// --- Card Component ---

interface ContentCardProps {
    type: FavoriteTargetType;
    id: number;
    title: string;
    subtitle?: string | null;
    image?: string | null;
    /** e.g. "Favorited Apr 7, 2026" or "Score: 15" */
    meta?: string | null;
    /** Rank number to show (for trending) */
    rank?: number;
    link?: string | null;
}

export const ContentCard = ({
    type,
    id,
    title,
    subtitle,
    image,
    meta,
    rank,
    link,
}: ContentCardProps) => {
    const card = (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs transition-shadow hover:shadow-md">
            {image && (
                <div className="h-40 w-full overflow-hidden">
                    <img
                        loading="lazy"
                        decoding="async"
                        src={image}
                        alt={title}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                        }}
                    />
                </div>
            )}
            <div className="p-4">
                <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {rank != null && (
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-600">
                                {rank}
                            </span>
                        )}
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${getBadgeColor(type)}`}
                        >
                            {getTargetLabel(type)}
                        </span>
                    </div>
                    <FavoriteButton target={buildFavoriteTarget(type, id)} size={14} />
                </div>
                <h3 className="line-clamp-2 text-sm font-semibold text-gray-900">{title}</h3>
                {subtitle && <p className="mt-1 line-clamp-1 text-xs text-gray-500">{subtitle}</p>}
                {meta && <p className="mt-2 text-xs text-gray-400">{meta}</p>}
            </div>
        </div>
    );

    return link ? (
        <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline"
            onClick={(e) => e.stopPropagation()}
        >
            {card}
        </a>
    ) : (
        card
    );
};
